import { useEffect, useRef, useState, useLayoutEffect } from "react";
import styled from "styled-components";
import { FaCopy, FaTimes } from "react-icons/fa";
import MathText from "../math/MathText";
import VimMarkdownEditor from "./VimMarkdownEditor";

export default function ExpandedEditorModal({
  editor,
  onClose,
  onCopy,
}) {
  const [vimModeEnabled, setVimModeEnabled] = useState(false);
  const activeEditorKeyRef = useRef(null);

  // Layout effect: choose Vim before the first paint, otherwise the plain white
  // textarea flashes before the dark Vim editor replaces it.
  useLayoutEffect(() => {
    if (!editor) {
      activeEditorKeyRef.current = null;
      return;
    }

    const editorKey = editor.key || editor.title;
    if (activeEditorKeyRef.current === editorKey) return;

    activeEditorKeyRef.current = editorKey;
    setVimModeEnabled(Boolean(editor.defaultVimMode));
  }, [editor]);

  useEffect(() => {
    if (typeof window === "undefined") return undefined;

    const shouldDisableTabSwitch = Boolean(editor && vimModeEnabled);
    window.__SUNNY_DISABLE_PERSISTENT_TAB_SWITCH__ = shouldDisableTabSwitch;

    return () => {
      if (window.__SUNNY_DISABLE_PERSISTENT_TAB_SWITCH__ === shouldDisableTabSwitch) {
        window.__SUNNY_DISABLE_PERSISTENT_TAB_SWITCH__ = false;
      }
    };
  }, [editor, vimModeEnabled]);

  if (!editor) return null;

  const showPreview = vimModeEnabled && Boolean(editor.preview);
  const previewSource = editor.previewSource ?? editor.value;
  const previewText = String(previewSource || "").trim();
  const isCodePreview = editor.previewType === "code";
  const hasCustomPreview = Boolean(editor.previewContent);

  return (
    <EditorOverlay>
      <ExpandedEditor $withPreview={showPreview} $withFooter={Boolean(editor.footerContent)} role="dialog" aria-modal="true" aria-label={editor.title}>
        <ExpandedHeader>
          <SectionTitle>{editor.title}</SectionTitle>
          <IconGroup>
            <TextIconButton
              type="button"
              title="切換 Vim 編輯"
              $active={vimModeEnabled}
              onClick={() => setVimModeEnabled((enabled) => !enabled)}
            >
              Vim
            </TextIconButton>
            <IconButton
              type="button"
              title="複製內容"
              onClick={() => onCopy(editor.value)}
            >
              <FaCopy />
            </IconButton>
            <IconButton type="button" title="關閉" onClick={onClose}>
              <FaTimes />
            </IconButton>
          </IconGroup>
        </ExpandedHeader>
        <EditorBody $withPreview={showPreview}>
          <EditorPane>
            {vimModeEnabled ? (
              <VimMarkdownEditor
                key={`${editor.key || editor.title}-${editor.syntaxMode || "markdown"}`}
                value={editor.value}
                onChange={editor.onChange}
                onCloseModal={onClose}
                onLeaderWrite={editor.onLeaderWrite}
                syntaxMode={editor.syntaxMode}
              />
            ) : (
              <ExpandedTextArea
                value={editor.value}
                onChange={(event) => editor.onChange(event.target.value)}
                autoFocus
              />
            )}
          </EditorPane>
          {showPreview && (
            <LatexPreviewPane>
              <PreviewTitle>
                {editor.previewTitle || (isCodePreview ? "程式碼預覽" : "預覽")}
              </PreviewTitle>
              <LatexPreviewContent
                $empty={!previewText && !hasCustomPreview}
                $code={isCodePreview}
                $custom={hasCustomPreview}
              >
                {hasCustomPreview ? (
                  editor.previewContent
                ) : isCodePreview ? (
                  previewText ? (
                    <pre>{previewText}</pre>
                  ) : (
                    <PreviewFallback>
                      {editor.previewFallback || "尚未輸入內容"}
                    </PreviewFallback>
                  )
                ) : (
                  <MathText
                    source={previewText}
                    fallback={editor.previewFallback || "尚未輸入內容"}
                  />
                )}
              </LatexPreviewContent>
            </LatexPreviewPane>
          )}
        </EditorBody>
        {editor.footerContent}
      </ExpandedEditor>
    </EditorOverlay>
  );
}

const EditorOverlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 2400;
  display: grid;
  place-items: center;
  padding: 16px;
  box-sizing: border-box;
  overflow: hidden;
  overscroll-behavior: contain;
  background: var(--app-modal-overlay, rgba(64, 64, 64, 0.72));

  @media (max-width: 640px) {
    padding: 10px;
  }
`;

const ExpandedEditor = styled.div`
  box-sizing: border-box;
  width: min(${({ $withPreview }) => ($withPreview ? "1480px" : "1120px")}, 100%);
  min-width: 0;
  min-height: 0;
  max-height: 100%;
  height: min(820px, calc(100dvh - 32px));
  overflow: hidden;
  display: grid;
  grid-template-rows: auto minmax(0, 1fr) ${({ $withFooter }) => $withFooter ? "auto" : ""};
  gap: 12px;
  border: 1px solid var(--app-border, #2b2b2b);
  border-radius: 8px;
  padding: 16px;
  background: var(--app-surface, #f8f8f8);
  color: var(--app-surface-text, #202124);
  box-shadow: 0 28px 80px rgba(0, 0, 0, 0.35);

  @media (max-width: 640px) {
    width: 100%;
    height: calc(100dvh - 20px);
    padding: 12px;
  }
`;

const ExpandedHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
`;

const SectionTitle = styled.h3`
  min-width: 0;
  overflow-wrap: anywhere;
  margin: 0;
  font-size: 15px;
  font-weight: 750;
`;

const IconGroup = styled.div`
  flex-shrink: 0;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const IconButton = styled.button`
  width: 38px;
  height: 38px;
  display: inline-grid;
  place-items: center;
  border: 1px solid var(--app-border, #c9c9c9);
  border-radius: 8px;
  background: var(--app-input-bg, #fff);
  color: var(--app-input-text, #3d3d3d);
  cursor: pointer;
  flex: 0 0 auto;
`;

const TextIconButton = styled.button`
  height: 38px;
  display: inline-grid;
  place-items: center;
  border: 1px solid
    ${({ $active }) => ($active ? "var(--app-nav-active-bg, #242424)" : "var(--app-border, #c9c9c9)")};
  border-radius: 8px;
  padding: 0 12px;
  background: ${({ $active }) =>
    $active ? "var(--app-nav-active-bg, #242424)" : "var(--app-input-bg, #fff)"};
  color: ${({ $active }) =>
    $active ? "var(--app-nav-active-text, #fff)" : "var(--app-input-text, #3d3d3d)"};
  font-size: 13px;
  font-weight: 750;
  cursor: pointer;
`;

const EditorBody = styled.div`
  min-width: 0;
  min-height: 0;
  display: grid;
  grid-template-columns: ${({ $withPreview }) =>
    $withPreview ? "minmax(0, 1fr) minmax(320px, 0.85fr)" : "minmax(0, 1fr)"};
  gap: 12px;

  @media (max-width: 920px) {
    grid-template-columns: minmax(0, 1fr);
    grid-template-rows: ${({ $withPreview }) => $withPreview ? "minmax(220px, 46dvh) minmax(200px, auto)" : "minmax(0, 1fr)"};
    overflow: auto;
    align-content: start;
    overscroll-behavior: contain;
  }
`;

const EditorPane = styled.div`
  min-width: 0;
  min-height: 0;
  height: 100%;
  overflow: hidden;


`;

const ExpandedTextArea = styled.textarea`
  box-sizing: border-box;
  min-width: 0;
  width: 100%;
  min-height: 0;
  height: 100%;
  resize: none;
  border: 1px solid var(--app-border, #cfcfcf);
  border-radius: 8px;
  padding: 14px;
  font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
  font-size: 14px;
  line-height: 1.6;
  background: var(--app-input-bg, #fff);
  color: var(--app-input-text, #202124);
`;

const LatexPreviewPane = styled.aside`
  min-width: 0;
  min-height: 0;
  display: grid;
  grid-template-rows: auto minmax(0, 1fr);
  border: 1px solid var(--app-border, #d7dce2);
  border-radius: 8px;
  background: var(--app-surface-hover, #fff);
  overflow: hidden;
`;

const PreviewTitle = styled.div`
  padding: 10px 12px;
  border-bottom: 1px solid var(--app-border, #e2e6eb);
  color: var(--app-subtle-text, #3c4043);
  font-size: 13px;
  font-weight: 850;
`;

const LatexPreviewContent = styled.div`
  min-width: 0;
  min-height: 0;
  overflow: auto;
  padding: ${({ $custom }) => ($custom ? "12px" : "14px")};
  background: ${({ $code }) => ($code ? "#1f1f1f" : "transparent")};
  color: ${({ $empty, $code }) =>
    $code
      ? "#f4f4f4"
      : $empty
        ? "var(--app-muted-text, #8b929e)"
        : "var(--app-surface-text, #202124)"};
  font-size: 15px;
  font-weight: ${({ $empty }) => ($empty ? 600 : 700)};
  line-height: 1.7;
  overflow-wrap: anywhere;
  white-space: pre-wrap;

  pre {
    margin: 0;
    white-space: pre-wrap;
    word-break: break-word;
    font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
    font-size: 13px;
    font-weight: 500;
    line-height: 1.6;
  }

  .katex-display {
    margin: 8px 0;
    overflow-x: auto;
    overflow-y: hidden;
  }
`;

const PreviewFallback = styled.div`
  color: var(--app-muted-text, #8b929e);
`;
